import {Button, Checkbox, FormControl, Link} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {SplashDialogImage} from '@github-ui/splash-dialog'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {COPILOT_CODING_AGENT_DOCS_URL} from '../../utils/constants'
import type {CopilotCodingAgentEligibility} from './copilot-assignment-types'
import styles from './CopilotCodingAgentRequestAccessDialog.module.css'

export const HIDE_CCA_REQUEST_DIALOG_KEY = 'hideCopilotCodingAgentRequestDialog'

export interface CopilotCodingAgentRequestAccessDialogProps {
  onClose: () => void
  eligibility: CopilotCodingAgentEligibility
}

export function CopilotCodingAgentRequestAccessDialog({
  onClose,
  eligibility,
}: CopilotCodingAgentRequestAccessDialogProps) {
  const [doNotShowAgain, setDoNotShowAgain] = useLocalStorage(HIDE_CCA_REQUEST_DIALOG_KEY, false)

  let buttonText = 'Request access'
  if (eligibility.canGrantEnterpriseCca || eligibility.canGrantOrgCca) {
    buttonText = 'Enable feature'
  } else if (eligibility.hasRequestedCca) {
    buttonText = 'Remove request'
  } else if (eligibility.canRequestCca) {
    buttonText = 'Request access'
  }

  let descriptionText =
    'Request access from your admin, and let GitHub Copilot work independently in the background to complete tasks, with Copilot coding agent.'

  if (eligibility.canGrantEnterpriseCca || eligibility.canGrantOrgCca) {
    descriptionText =
      'Enable Copilot coding agent to let GitHub Copilot work independently in the background to complete tasks.'
  } else if (eligibility.hasRequestedCca) {
    descriptionText = 'You have requested access to Copilot coding agent. Your admin will review your request.'
  }

  return (
    <Dialog
      onClose={onClose}
      width="large"
      renderHeader={({dialogLabelId}) => (
        <>
          <div className={styles.headerContainer}>
            <SplashDialogImage
              darkImagePath="/static/images/modules/swe_agent/agent-task-dark.png"
              lightImagePath="/static/images/modules/swe_agent/agent-task-light.png"
              // eslint-disable-next-line @github-ui/github-monorepo/no-sx -- SplashDialogImage from @github-ui/splash-dialog only accepts sx prop
              sx={{
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
              }}
            />
            <div className={styles.closeButtonContainer}>
              <Dialog.CloseButton onClose={onClose} />
            </div>
          </div>
          <Dialog.Header id={dialogLabelId} className={styles.visuallyHidden}>
            <Dialog.Title>Copilot coding agent is disabled</Dialog.Title>
          </Dialog.Header>
        </>
      )}
    >
      <div className={styles.body}>
        <h2 className={styles.heading}>Copilot coding agent is disabled</h2>
        <p className={styles.description}>
          {descriptionText}{' '}
          <Link href={COPILOT_CODING_AGENT_DOCS_URL} target="_blank" rel="noreferrer">
            Learn more
          </Link>
          .
        </p>
        <div className={styles.footer}>
          <FormControl>
            <Checkbox checked={doNotShowAgain} onChange={e => setDoNotShowAgain(e.currentTarget.checked)} />
            <FormControl.Label>Don&apos;t show this again</FormControl.Label>
          </FormControl>
          <Button onClick={() => {}}>{buttonText}</Button>
        </div>
      </div>
    </Dialog>
  )
}

try{ CopilotCodingAgentRequestAccessDialog.displayName ||= 'CopilotCodingAgentRequestAccessDialog' } catch {}